/*!
 * styled-select.js
 * Progressively enhances <select data-styled-select> elements into an
 * accessible, fully-styleable custom dropdown.
 *
 * A11y approach: implements the WAI-ARIA APG "select-only combobox" pattern
 * (https://www.w3.org/WAI/ARIA/apg/patterns/combobox/examples/combobox-select-only/)
 * - role="combobox" trigger with aria-expanded / aria-controls / aria-activedescendant
 * - role="listbox" popup with role="option" children (aria-selected)
 * - full keyboard support: Enter/Space/Arrows to open, Arrow Up/Down to move
 *   and select (mirrors native <select> live-update), Home/End, type-ahead,
 *   Escape to cancel, Tab/click-outside to close
 * - the original <select> is kept in the DOM (aria-hidden, tabindex="-1") so
 *   forms, autofill, and any existing JS bound to it keep working unchanged
 *
 * Usage: just add the attribute to a normal <select>, keep its <label for="...">
 * (or aria-label/aria-labelledby) as usual, and include styled-select.css.
 *
 * <label for="fruit">Fruit</label>
 * <select id="fruit" data-styled-select>
 *   <option value="">Choose one</option>
 *   <option value="apple">Apple</option>
 * </select>
 *
 * No configuration needed: the script auto-scans the document on load and
 * watches for elements added later. Multi-select (<select multiple>) is not
 * supported and is left untouched.
 */
(function () {
    'use strict';

    var instances = new WeakMap();
    var idCounter = 0;

    function uniqueId(prefix) {
        idCounter += 1;
        return prefix + '-' + idCounter + '-' + Math.random().toString(36).slice(2, 7);
    }

    function StyledSelect(select) {
        if (instances.has(select)) return instances.get(select);
        if (select.multiple) {
            console.warn('[styled-select] <select multiple> is not supported, skipping', select);
            return null;
        }

        this.select = select;
        this.options = [];
        this.activeIndex = -1;
        this.open = false;
        this.typeahead = '';
        this.typeaheadTimer = null;
        this._activeEl = null;
        this._preOpenIndex = -1;
        this._onDocumentMouseDown = this._onDocumentMouseDown.bind(this);

        this._build();
        instances.set(select, this);
        return this;
    }

    StyledSelect.prototype._build = function () {
        var select = this.select;
        select.setAttribute('data-styled-select-initialized', '');

        var wrapper = document.createElement('div');
        wrapper.className = 'styled-select';
        select.parentNode.insertBefore(wrapper, select);
        wrapper.appendChild(select);

        // Keep the native select for form submission / autofill, but remove it
        // from the accessibility tree since the combobox below replaces it.
        select.classList.add('styled-select__native');
        select.setAttribute('aria-hidden', 'true');
        select.setAttribute('tabindex', '-1');

        var comboId = select.id ? select.id + '-combobox' : uniqueId('styled-select-combobox');
        var listboxId = uniqueId('styled-select-listbox');
        var labelledBy = this._reassociateLabel(select, comboId);

        var combo = document.createElement('div');
        combo.className = 'styled-select__control';
        combo.id = comboId;
        combo.setAttribute('role', 'combobox');
        combo.setAttribute('aria-haspopup', 'listbox');
        combo.setAttribute('aria-expanded', 'false');
        combo.setAttribute('aria-controls', listboxId);
        combo.tabIndex = select.disabled ? -1 : 0;
        if (select.disabled) combo.setAttribute('aria-disabled', 'true');
        if (select.hasAttribute('required')) combo.setAttribute('aria-required', 'true');

        if (labelledBy) {
            combo.setAttribute('aria-labelledby', labelledBy + ' ' + comboId);
        } else if (select.getAttribute('aria-label')) {
            combo.setAttribute('aria-label', select.getAttribute('aria-label'));
        } else if (select.getAttribute('aria-labelledby')) {
            combo.setAttribute('aria-labelledby', select.getAttribute('aria-labelledby'));
        } else {
            console.warn('[styled-select] no associated <label>, aria-label, or aria-labelledby found for', select);
        }

        var valueSpan = document.createElement('span');
        valueSpan.className = 'styled-select__value';

        var arrow = document.createElement('span');
        arrow.className = 'styled-select__arrow';
        arrow.setAttribute('aria-hidden', 'true');

        combo.appendChild(valueSpan);
        combo.appendChild(arrow);

        var listbox = document.createElement('ul');
        listbox.className = 'styled-select__listbox';
        listbox.id = listboxId;
        listbox.setAttribute('role', 'listbox');
        listbox.tabIndex = -1;
        if (labelledBy) listbox.setAttribute('aria-labelledby', labelledBy);
        listbox.hidden = true;

        wrapper.appendChild(combo);
        wrapper.appendChild(listbox);

        this.wrapper = wrapper;
        this.combo = combo;
        this.valueSpan = valueSpan;
        this.listbox = listbox;

        this._renderOptions();
        this._syncFromSelect();
        this._attachEvents();
    };

    // If the select has an existing <label for="select-id">, repoint it at the
    // new combobox so clicking/activating the label still focuses the widget.
    StyledSelect.prototype._reassociateLabel = function (select, comboId) {
        if (!select.id) return null;
        var label = document.querySelector('label[for="' + CSS.escape(select.id) + '"]');
        if (!label) return null;
        var labelId = label.id || uniqueId('styled-select-label');
        label.id = labelId;
        label.setAttribute('for', comboId);
        return labelId;
    };

    StyledSelect.prototype._renderOptions = function () {
        var self = this;
        this.listbox.innerHTML = '';
        this.options = [];

        Array.prototype.forEach.call(this.select.children, function (child) {
            if (child.tagName === 'OPTGROUP') {
                var groupLi = document.createElement('li');
                groupLi.setAttribute('role', 'presentation');

                var groupLabel = document.createElement('div');
                groupLabel.className = 'styled-select__group-label';
                groupLabel.textContent = child.label;
                groupLi.appendChild(groupLabel);

                var groupList = document.createElement('ul');
                groupList.className = 'styled-select__group';
                groupList.setAttribute('role', 'group');
                groupList.setAttribute('aria-label', child.label);
                Array.prototype.forEach.call(child.children, function (opt) {
                    if (opt.tagName === 'OPTION') groupList.appendChild(self._createOption(opt));
                });

                groupLi.appendChild(groupList);
                self.listbox.appendChild(groupLi);
            } else if (child.tagName === 'OPTION') {
                self.listbox.appendChild(self._createOption(child));
            }
        });
    };

    StyledSelect.prototype._createOption = function (optionEl) {
        var self = this;
        var disabled = optionEl.disabled;

        var li = document.createElement('li');
        li.id = uniqueId('styled-select-option');
        li.setAttribute('role', 'option');
        li.className = 'styled-select__option';
        li.textContent = optionEl.textContent;
        li.dataset.value = optionEl.value;
        li.setAttribute('aria-selected', optionEl.selected ? 'true' : 'false');
        if (disabled) {
            li.setAttribute('aria-disabled', 'true');
            li.classList.add('styled-select__option--disabled');
        }

        var entry = { el: li, optionEl: optionEl, disabled: disabled };

        li.addEventListener('click', function () {
            if (disabled) return;
            self._commit(self.options.indexOf(entry));
            self._close();
            self.combo.focus();
        });

        this.options.push(entry);
        return li;
    };

    StyledSelect.prototype._syncFromSelect = function () {
        var self = this;
        var selectedIndex = this.select.selectedIndex;
        this.options.forEach(function (entry, i) {
            var isSelected = i === selectedIndex;
            entry.el.setAttribute('aria-selected', isSelected ? 'true' : 'false');
            entry.el.classList.toggle('styled-select__option--selected', isSelected);
            if (isSelected) self.activeIndex = i;
        });
        var selectedOption = this.select.options[selectedIndex];
        this.valueSpan.textContent = selectedOption ? selectedOption.textContent : '';
    };

    StyledSelect.prototype._attachEvents = function () {
        var self = this;

        this.combo.addEventListener('click', function () {
            if (self.select.disabled) return;
            if (self.open) self._close();
            else self._openList();
        });

        this.combo.addEventListener('keydown', function (e) { self._onKeyDown(e); });

        // Keep the widget in sync if something else changes the native select
        // (e.g. form.reset(), or external code setting .value and firing 'change').
        this.select.addEventListener('change', function () { self._syncFromSelect(); });
    };

    StyledSelect.prototype._onDocumentMouseDown = function (e) {
        if (!this.wrapper.contains(e.target)) this._close();
    };

    StyledSelect.prototype._openList = function () {
        if (this.select.disabled || this.open) return;
        this.open = true;
        this.listbox.hidden = false;
        this.combo.setAttribute('aria-expanded', 'true');
        this._preOpenIndex = this.activeIndex;
        var startIndex = this.activeIndex >= 0 ? this.activeIndex : this._firstEnabledIndex();
        this._setActive(startIndex);
        document.addEventListener('mousedown', this._onDocumentMouseDown);
    };

    StyledSelect.prototype._close = function () {
        if (!this.open) return;
        this.open = false;
        this.listbox.hidden = true;
        this.combo.setAttribute('aria-expanded', 'false');
        this.combo.removeAttribute('aria-activedescendant');
        document.removeEventListener('mousedown', this._onDocumentMouseDown);
    };

    StyledSelect.prototype._setActive = function (index) {
        if (index < 0 || index >= this.options.length) return;
        var entry = this.options[index];
        if (this._activeEl) this._activeEl.classList.remove('styled-select__option--active');
        entry.el.classList.add('styled-select__option--active');
        this._activeEl = entry.el;
        this.combo.setAttribute('aria-activedescendant', entry.el.id);
        if (typeof entry.el.scrollIntoView === 'function') {
            entry.el.scrollIntoView({ block: 'nearest' });
        }
    };

    // Commits a value: updates ARIA state, visible label, and the underlying
    // native <select> (dispatching 'change' so external listeners still fire).
    StyledSelect.prototype._commit = function (index) {
        if (index < 0 || index >= this.options.length) return;
        var entry = this.options[index];
        if (entry.disabled) return;

        this.activeIndex = index;
        this.options.forEach(function (o) {
            var selected = o === entry;
            o.el.setAttribute('aria-selected', selected ? 'true' : 'false');
            o.el.classList.toggle('styled-select__option--selected', selected);
        });
        this.valueSpan.textContent = entry.optionEl.textContent;

        if (this.select.value !== entry.optionEl.value) {
            this.select.value = entry.optionEl.value;
            this.select.dispatchEvent(new Event('change', { bubbles: true }));
        }
    };

    StyledSelect.prototype._onKeyDown = function (e) {
        if (this.select.disabled) return;
        var key = e.key;

        if (!this.open) {
            if (key === 'ArrowDown' || key === 'ArrowUp' || key === 'Enter' || key === ' ') {
                e.preventDefault();
                this._openList();
                return;
            }
            if (key === 'Home') { e.preventDefault(); this._commit(this._firstEnabledIndex()); return; }
            if (key === 'End') { e.preventDefault(); this._commit(this._lastEnabledIndex()); return; }
            if (key.length === 1 && key !== ' ') { this._typeahead(key); }
            return;
        }

        switch (key) {
            case 'Escape':
                e.preventDefault();
                this._commit(this._preOpenIndex);
                this._close();
                break;
            case 'Enter':
            case ' ':
                e.preventDefault();
                this._close();
                break;
            case 'ArrowDown':
                e.preventDefault();
                this._moveActive(1);
                break;
            case 'ArrowUp':
                e.preventDefault();
                this._moveActive(-1);
                break;
            case 'Home':
                e.preventDefault();
                this._setActive(this._firstEnabledIndex());
                this._commit(this._firstEnabledIndex());
                break;
            case 'End':
                e.preventDefault();
                this._setActive(this._lastEnabledIndex());
                this._commit(this._lastEnabledIndex());
                break;
            case 'Tab':
                this._close();
                break;
            default:
                if (key.length === 1) this._typeahead(key);
        }
    };

    StyledSelect.prototype._moveActive = function (direction) {
        var next = this.activeIndex;
        do {
            next += direction;
            if (next < 0 || next >= this.options.length) return;
        } while (this.options[next].disabled);
        this._setActive(next);
        this._commit(next);
    };

    StyledSelect.prototype._firstEnabledIndex = function () {
        for (var i = 0; i < this.options.length; i++) if (!this.options[i].disabled) return i;
        return -1;
    };

    StyledSelect.prototype._lastEnabledIndex = function () {
        for (var i = this.options.length - 1; i >= 0; i--) if (!this.options[i].disabled) return i;
        return -1;
    };

    StyledSelect.prototype._typeahead = function (char) {
        var self = this;
        this.typeahead += char.toLowerCase();
        clearTimeout(this.typeaheadTimer);
        this.typeaheadTimer = setTimeout(function () { self.typeahead = ''; }, 500);

        var count = this.options.length;
        var start = (this.activeIndex + 1) % count;
        for (var i = 0; i < count; i++) {
            var index = (start + i) % count;
            var entry = this.options[index];
            if (!entry.disabled && entry.optionEl.textContent.toLowerCase().indexOf(this.typeahead) === 0) {
                if (this.open) { this._setActive(index); this._commit(index); }
                else { this._commit(index); }
                break;
            }
        }
    };

    // Call after changing options/disabled state on the native select via other
    // JS, so the enhanced widget picks up the changes.
    StyledSelect.prototype.refresh = function () {
        this.combo.tabIndex = this.select.disabled ? -1 : 0;
        if (this.select.disabled) this.combo.setAttribute('aria-disabled', 'true');
        else this.combo.removeAttribute('aria-disabled');
        this._renderOptions();
        this._syncFromSelect();
    };

    function enhanceAll(root) {
        var scope = root && root.querySelectorAll ? root : document;
        var selects = scope.querySelectorAll('select[data-styled-select]:not([data-styled-select-initialized])');
        Array.prototype.forEach.call(selects, function (select) { new StyledSelect(select); });
    }

    function ready(fn) {
        if (document.readyState !== 'loading') fn();
        else document.addEventListener('DOMContentLoaded', fn);
    }

    ready(function () {
        enhanceAll(document);

        if (typeof MutationObserver !== 'undefined') {
            var observer = new MutationObserver(function (mutations) {
                mutations.forEach(function (mutation) {
                    mutation.addedNodes.forEach(function (node) {
                        if (node.nodeType !== 1) return;
                        if (node.matches && node.matches('select[data-styled-select]')) {
                            new StyledSelect(node);
                        } else if (node.querySelectorAll) {
                            enhanceAll(node);
                        }
                    });
                });
            });
            observer.observe(document.body, { childList: true, subtree: true });
        }
    });

    window.StyledSelect = {
        init: enhanceAll,
        get: function (select) { return instances.get(select); }
    };
})();